import Axios from "axios";

const esTokenValido = (token) => {
  if (!token) return false;
  const normalized = token.toString().trim().toLowerCase();
  return !["undefined", "null", "nan", "[object object]", ""].includes(normalized);
};

// Mismo token que usa HttpService. Se exporta para las llamadas que no pasan por
// axios (descargas con fetch), de modo que todas manden las mismas cabeceras y no
// dependan solo de la cookie de sesion, que en cross-origin puede no viajar.
export const getSessionToken = () => {
  const localToken = localStorage.getItem("token");
  if (esTokenValido(localToken)) return localToken.toString().trim();

  const sessionToken = sessionStorage.getItem("token");
  if (esTokenValido(sessionToken)) return sessionToken.toString().trim();

  return null;
};

export const getAuthHeaders = () => {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}`, "x-session-token": token } : {};
};

export class HttpService {
  constructor(baseURL) {
    this._axios = Axios.create({
      baseURL: baseURL,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });


    this._axios.interceptors.request.use(this.onRequestSuccess, (error) =>
      Promise.reject(error)
      
    );

    this._axios.interceptors.response.use(this.onResponseSuccess, (error) =>
      this.onResponseError(error)
    );

  }


  isValidToken = (token) => {
    if (!token) return false;
    const normalized = token.toString().trim().toLowerCase();
    return !["undefined", "null", "nan", "[object object]", ""].includes(normalized);
  };

  getSessionToken = () => {
    const localToken = localStorage.getItem("token");
    if (this.isValidToken(localToken)) {
      return localToken.toString().trim();
    }

    const sessionToken = sessionStorage.getItem("token");
    if (this.isValidToken(sessionToken)) {
      return sessionToken.toString().trim();
    }

    return null;
  };

  onRequestSuccess = (config) => {
    const token = this.getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["x-session-token"] = token;
    } else {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      if (config.headers?.["x-session-token"]) {
        delete config.headers["x-session-token"];
      }
    }
    return config;
  };

  onResponseSuccess = (response) => {
    const sessionToken = response?.headers?.["x-session-token"] || response?.data?.token;
    if (this.isValidToken(sessionToken)) {
      localStorage.setItem("token", sessionToken);
      sessionStorage.setItem("token", sessionToken);
    }
    return response;
  };


  // Maneja los errores de autenticación y desloguea el usuario
  onResponseError = (error) => { 
    const status = error.response?.data;
    // Verifica si es un error de autenticación
    if (status?.logout === true) {
      localStorage.setItem("logout", JSON.stringify(true));
      
    }
    return Promise.reject(error);
  };

  
  // Métodos de solicitud
  post = async (url, data, isFile = false, extraConfig = {}) => {
    const options = this.getOptionsConfig("post", url, data, {
      headers: {
        "Content-Type": isFile ? "multipart/form-data" : "application/json",
      },
      ...extraConfig,
    });
    return await this.request(options);
  };

  get = async (url) => {
    const options = this.getOptionsConfig("get", url);
    return await this.request(options);
  };

  patch = async (url, data) => {
    const options = this.getOptionsConfig("patch", url, data);
    return await this.request(options);
  };

  getOptionsConfig = (method, url, data, config = {}) => ({
    method,
    url,
    data,
    ...config,
  });

  /**
   * Normaliza la respuesta de un error para que el llamador reciba SIEMPRE un
   * objeto con `error` o `message`, que es lo que comprueban las vistas.
   *
   * Antes se resolvía `ex.response?.data` tal cual, lo que provocaba dos fallos:
   *
   *  - Sin respuesta (red caída, CORS, timeout) resolvía `undefined`, y los 48
   *    sitios que leen `response.message` sin protección reventaban.
   *  - En una descarga (responseType blob) el cuerpo del error llegaba como Blob
   *    con un JSON dentro, y el llamador lo guardaba como si fuera el archivo:
   *    de ahí los PDF que no abrían.
   */
  normalizarError = async (ex) => {
    const respuesta = ex?.response;
    const cuerpo = respuesta?.data;
    const httpStatus = respuesta?.status ?? 0;
    const conEstado = (objeto) =>
      objeto && typeof objeto === "object" && !Array.isArray(objeto) && objeto.httpStatus === undefined
        ? { ...objeto, httpStatus }
        : objeto;

    // Descarga fallida: el error viaja como Blob porque se pidió responseType blob.
    if (typeof Blob !== "undefined" && cuerpo instanceof Blob) {
      try {
        const texto = await cuerpo.text();
        return conEstado(JSON.parse(texto));
      } catch (e) {
        return { error: "El servidor no devolvió un archivo válido", httpStatus };
      }
    }

    if (cuerpo && typeof cuerpo === "object") return conEstado(cuerpo);
    if (typeof cuerpo === "string" && cuerpo.trim()) return { error: cuerpo, httpStatus };

    if (httpStatus === 401 || httpStatus === 403) {
      return { error: "Tu sesión expiró. Vuelve a iniciar sesión.", httpStatus };
    }

    return {
      error: ex?.message || "No se pudo conectar con el servidor",
      httpStatus,
    };
  };

  request(options) {
    return new Promise((resolve) => {
      this._axios
        .request(options)
        .then((res) => resolve(res.data))
        .catch((ex) => this.normalizarError(ex).then(resolve));
    });
  }
}

export default HttpService;
